import { useIsMobile } from '@/hooks/use-mobile';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  ShoppingCart,
  ShoppingBag,
  Users,
  Briefcase,
  LifeBuoy,
  Percent,
  Ticket,
  Settings,
  LogOut,
  Menu,
  X,
  CreditCard,
  DollarSign,
  Bell,
  Megaphone,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Building2,
  Smartphone,
  FileText,
  Palette,
  Images,
  Star,
  Wifi,
  HelpCircle,
  BarChart3,
  Clock,
  History,
  RefreshCw,
  Shield,
  FolderTree,
  Truck,
  LayoutGrid,
  ClipboardList,
  Calendar,
  Wrench,
  Package,
  Map,
  MapPin,
  UserCheck,
  Locate,
  Car,
  Wallet,
  RotateCcw,
  ShieldCheck,
  Flame,
  Globe2,
} from 'lucide-react';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect, useState, useMemo, useId, useRef } from 'react';
import { useAuthStore, type UserRole } from '@/store/authStore';
import { useTenantContext } from '@/hooks/useTenantContext';
import { useBrandingTheme } from '@/hooks/useBrandingTheme';
import { useProviderCapabilities } from '@/hooks/useProviderCapabilities';
import { isProviderPathAllowed } from '@/lib/provider-capabilities.mjs';
import { useTenantGovernance } from '@/hooks/useTenantGovernance';
import { isPrivateBusinessPath } from '@/lib/business-privacy-navigation';
import { useEnabledModules } from '@/hooks/useEnabledModules';
import { isTenantPathRelevant } from '@/lib/tenant-capabilities.mjs';
import type { TenantCapabilities } from '@/lib/tenant-capabilities.mjs';
import { requiredModuleForPath } from '@/lib/moduleNav';
import { useUIStore } from '@/store/uiStore';
import { activeNavigationHref, groupPlatformSidebar, groupProviderSidebar, groupTenantSidebar } from '@/lib/sidebar-navigation';

interface SidebarItem {
  icon: React.ComponentType<{ className?: string }>;
  labelKey: string;
  href?: string;
  children?: { icon: React.ComponentType<{ className?: string }>; labelKey: string; href: string }[];
}

// SuperAdmin menu - Platform-level management
// Note: Tenant Admins and Subscriptions are managed inside Tenant Details
const superAdminItems: SidebarItem[] = [
  { icon: LayoutDashboard, labelKey: 'layout.sidebar.platformDashboard', href: '/' },
  { icon: Building2, labelKey: 'layout.sidebar.tenants', href: '/tenants' },
  { icon: BarChart3, labelKey: 'layout.sidebar.analytics', href: '/analytics' },
  { icon: RefreshCw, labelKey: 'layout.sidebar.recurringJobs', href: '/recurring-jobs' },
  {
    icon: FileText,
    labelKey: 'layout.sidebar.platformData',
    children: [
      { icon: Globe2, labelKey: 'layout.sidebar.countries', href: '/countries' },
      { icon: Building2, labelKey: 'layout.sidebar.cities', href: '/cities' },
      { icon: Users, labelKey: 'layout.sidebar.nationalities', href: '/nationalities' },
    ],
  },
  { icon: Bell, labelKey: 'layout.sidebar.notificationSettings', href: '/notification-settings' },
  { icon: Flame, labelKey: 'platformFirebase.title', href: '/platform/firebase' },
  { icon: Settings, labelKey: 'layout.sidebar.platformSettings', href: '/platform-settings' },
];

// TenantAdmin menu - Grouped by domain for clean navigation
const tenantAdminItems: SidebarItem[] = [
  { icon: LayoutDashboard, labelKey: 'launchReadiness.title', href: '/launch' },
  // â”€â”€ Overview â”€â”€
  { icon: LayoutDashboard, labelKey: 'layout.sidebar.dashboard', href: '/' },

  // â”€â”€ Orders â”€â”€
  { icon: ShoppingCart, labelKey: 'layout.sidebar.orders', href: '/orders' },
  { icon: ShoppingBag, labelKey: 'shopOperations.orders', href: '/shop-orders' },

  // â”€â”€ People â”€â”€
  {
    icon: Users,
    labelKey: 'layout.sidebar.people',
    children: [
      { icon: Users, labelKey: 'layout.sidebar.customers', href: '/customers' },
      { icon: Briefcase, labelKey: 'layout.sidebar.providers', href: '/providers' },
      { icon: ShieldCheck, labelKey: 'layout.sidebar.providerApproval', href: '/providers/approval' },
      { icon: Wifi, labelKey: 'layout.sidebar.providerStatus', href: '/providers/status' },
      { icon: CreditCard, labelKey: 'layout.sidebar.debts', href: '/providers/debts' },
    ],
  },

  // â”€â”€ Catalog â”€â”€
  {
    icon: FolderTree,
    labelKey: 'layout.sidebar.catalogManagement',
    children: [
      { icon: FolderTree, labelKey: 'layout.sidebar.categories', href: '/categories' },
      { icon: Wrench, labelKey: 'layout.sidebar.services', href: '/services' },
      { icon: ShoppingBag, labelKey: 'layout.sidebar.products', href: '/products' },
      { icon: Truck, labelKey: 'layout.sidebar.dispatchers', href: '/dispatchers' },
    ],
  },

  // â”€â”€ Finance â”€â”€
  {
    icon: DollarSign,
    labelKey: 'layout.sidebar.finance',
    children: [
      { icon: DollarSign, labelKey: 'layout.sidebar.transactions', href: '/transactions' },
      { icon: Star, labelKey: 'layout.sidebar.reviews', href: '/reviews' },
    ],
  },

  // â”€â”€ Support â”€â”€
  {
    icon: LifeBuoy,
    labelKey: 'layout.sidebar.support',
    children: [
      { icon: LifeBuoy, labelKey: 'layout.sidebar.tickets', href: '/tickets' },
      { icon: Clock, labelKey: 'layout.sidebar.supportHours', href: '/support-hours' },
      { icon: HelpCircle, labelKey: 'layout.sidebar.faqs', href: '/faqs' },
    ],
  },

  // â”€â”€ Maps â”€â”€
  {
    icon: MapPin,
    labelKey: 'layout.sidebar.maps',
    children: [
      { icon: MapPin, labelKey: 'layout.sidebar.ordersMap', href: '/maps/orders' },
      { icon: Users, labelKey: 'layout.sidebar.providersMap', href: '/maps/providers' },
      { icon: Locate, labelKey: 'layout.sidebar.nearby', href: '/maps/nearby' },
    ],
  },

  // â”€â”€ Marketing â”€â”€
  {
    icon: Megaphone,
    labelKey: 'layout.sidebar.marketing',
    children: [
      { icon: Ticket, labelKey: 'layout.sidebar.coupons', href: '/coupons' },
      { icon: Percent, labelKey: 'layout.sidebar.offers', href: '/offers' },
      { icon: Bell, labelKey: 'layout.sidebar.notifications', href: '/notifications' },
    ],
  },

  // â”€â”€ Notifications â”€â”€
  {
    icon: Bell,
    labelKey: 'layout.sidebar.notificationManagement',
    children: [
      { icon: Bell, labelKey: 'layout.sidebar.notificationSettings', href: '/notification-settings' },
      { icon: History, labelKey: 'layout.sidebar.notificationLogs', href: '/notification-logs' },
    ],
  },

  // â”€â”€ Branding â”€â”€
  {
    icon: Palette,
    labelKey: 'layout.sidebar.branding',
    children: [
      { icon: Palette, labelKey: 'layout.sidebar.brandIdentity', href: '/branding' },
      { icon: Images, labelKey: 'layout.sidebar.welcomeSliders', href: '/branding/sliders' },
    ],
  },

  // â”€â”€ System â”€â”€
  {
    icon: Settings,
    labelKey: 'layout.sidebar.system',
    children: [
      { icon: Shield, labelKey: 'governance.title', href: '/governance' },
      { icon: Settings, labelKey: 'layout.sidebar.systemConfig', href: '/config' },
      { icon: Smartphone, labelKey: 'layout.sidebar.appVersions', href: '/app-versions' },
      { icon: RefreshCw, labelKey: 'layout.sidebar.recurringJobs', href: '/recurring-jobs' },
      { icon: Shield, labelKey: 'layout.sidebar.usersSecurity', href: '/users-security' },
      { icon: Settings, labelKey: 'layout.sidebar.settings', href: '/settings' },
    ],
  },
];

// ProviderAdmin menu - the master list. Actual sidebar is filtered by the
// provider's additive capabilities and tenant module/channel entitlements.
const providerAdminItems: SidebarItem[] = [
  { icon: LayoutDashboard, labelKey: 'providerAdmin.dashboard', href: '/business' },
  { icon: ClipboardList, labelKey: 'providerAdmin.orders', href: '/business/orders' },
  { icon: ShoppingBag, labelKey: 'providerAdmin.shopOrders', href: '/business/shop-orders' },
  { icon: Truck, labelKey: 'retailOperations.manageDelivery', href: '/business/deliveries' },
  { icon: RotateCcw, labelKey: 'providerAdmin.returns', href: '/business/returns' },
  { icon: Users, labelKey: 'providerAdmin.team', href: '/business/team' },
  { icon: Calendar, labelKey: 'providerAdmin.calendar', href: '/business/calendar' },
  { icon: Calendar, labelKey: 'domainUi.visitScheduling', href: '/business/visit-schedule' },
  { icon: Wrench, labelKey: 'providerAdmin.services', href: '/business/services' },
  { icon: Package, labelKey: 'providerAdmin.products', href: '/business/products' },
  { icon: FileText, labelKey: 'merchantPayables.title', href: '/business/payables' },
  { icon: FileText, labelKey: 'merchantSupplierCredits.title', href: '/business/supplier-credits' },
  { icon: Package, labelKey: 'merchantSupplierReturns.title', href: '/business/supplier-stock-returns' },
  { icon: FileText, labelKey: 'merchantSupplierAdvances.title', href: '/business/supplier-advances' },
  { icon: FileText, labelKey: 'merchantBank.title', href: '/business/bank-reconciliation' },
  { icon: Package, labelKey: 'merchantWarehouses.title', href: '/business/warehouses' },
  { icon: ClipboardList, labelKey: 'merchantStocktake.title', href: '/business/stocktakes' },
  { icon: Package, labelKey: 'merchantStaff.title', href: '/business/staff-access' },

  { icon: Package, labelKey: 'merchantImport.title', href: '/business/catalog-imports' },
  { icon: Package, labelKey: 'merchantTrade.title', href: '/business/trade' },
  { icon: Percent, labelKey: 'merchantPromotions.title', href: '/business/promotions' },
  { icon: Package, labelKey: 'merchantReplenishment.title', href: '/business/replenishment' },
  { icon: Package, labelKey: 'merchantInventory.title', href: '/business/inventory' },
  { icon: Package, labelKey: 'merchantPurchasing.title', href: '/business/purchasing' },
  // Unified workbench (BZ3, 2026-05-23) â€” replaces the previous three
  // separate items: dispatch, fleet (live map), and board (kanban).
  { icon: LayoutGrid, labelKey: 'providerAdmin.operations', href: '/business/operations' },
  { icon: Map, labelKey: 'providerAdmin.liveMap', href: '/business/map' },
  { icon: MapPin, labelKey: 'providerAdmin.zones', href: '/business/zones' },
  { icon: Car, labelKey: 'providerAdmin.vehicles', href: '/business/vehicles' },
  { icon: Wallet, labelKey: 'providerAdmin.wallets', href: '/business/wallets' },
  { icon: BarChart3, labelKey: 'providerAdmin.reports', href: '/business/reports' },
  // BZ7 (2026-05-24): driver scorecards. Visible to team segment only â€” solo / salon / shop
  // segments don't have drivers, so the page would render an empty state.
  { icon: BarChart3, labelKey: 'providerAdmin.scorecards', href: '/business/scorecards' },
  { icon: DollarSign, labelKey: 'providerAdmin.finance', href: '/business/finance' },
  // BZ8 (2026-05-24): cash-on-delivery reconciliation. Shown for shops + team
  // (drivers carry cash); salon and solo segments rarely run cash-collection workflows.
  { icon: DollarSign, labelKey: 'providerAdmin.cashReconciliation', href: '/business/cash-reconciliation' },
  { icon: UserCheck, labelKey: 'providerAdmin.customers', href: '/business/customers' },
  { icon: Settings, labelKey: 'providerAdmin.settings', href: '/business/settings' },
  { icon: Shield, labelKey: 'governance.title', href: '/business/support-access' },
  { icon: Shield, labelKey: 'verificationWorkspace.title', href: '/business/verification' },
  { icon: Building2, labelKey: 'membership.title', href: '/business-workspaces' },
];

/**
 * Filter the master provider-admin sidebar to what actually applies to
 * this owner's business. A solo plumber doesn't need /vehicles or
 * /shop-orders; a salon doesn't need /fleet or /vehicles; a shop doesn't
 * need /calendar or /zones. Cuts the visible nav by 30-50% per segment.
 *
 * Hrefs are the contract â€” we filter by URL path so renames in `labelKey`
 * don't break this logic.
 */
function filterProviderAdminItems(
  items: SidebarItem[],
  capabilities: Record<string, unknown>,
  ready: boolean,
): SidebarItem[] {
  const safeBeforeResolution = new Set(['/business', '/business/settings', '/business/verification']);
  return items.filter((item) => item.href && (
    ready
      ? isProviderPathAllowed(item.href, capabilities)
      : safeBeforeResolution.has(item.href)
  ));
}

/**
 * Hide tenant-admin nav entries whose module is disabled for this tenant, so
 * admins don't land on a page the backend will 403. Drops module-gated leaf
 * items and any parent group left empty. Cross-cutting items (no module
 * mapping) always pass.
 */
function filterTenantItemsByModules(
  items: SidebarItem[],
  isManageable: (moduleKey: string) => boolean,
  capabilities: TenantCapabilities,
): SidebarItem[] {
  const leafAllowed = (href?: string) => {
    if (href === '/orders' && capabilities.shop && !capabilities.services) return false;
    const moduleKey = href ? requiredModuleForPath(href) : undefined;
    return (!moduleKey || isManageable(moduleKey)) && (!href || isTenantPathRelevant(href, capabilities));
  };

  return items
    .map((item) =>
      item.children
        ? { ...item, children: item.children.filter((child) => leafAllowed(child.href)) }
        : item,
    )
    .filter((item) =>
      item.children ? item.children.length > 0 : leafAllowed(item.href),
    );
}

function filterTenantItemsByRole(items: SidebarItem[], role: UserRole): SidebarItem[] {
  if (role !== 'support') return items;

  return items
    .map((item) => item.children
      ? { ...item, children: item.children.filter((child) => child.href !== '/notification-settings' && child.href !== '/recurring-jobs') }
      : item)
    .filter((item) => item.children ? item.children.length > 0 : item.href !== '/launch');
}

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({ isOpen = false, onClose }: SidebarProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const { logout, tenantId, impersonatedTenantId, providerId, user } = useAuthStore();
  const { language } = useUIStore();
  const { role } = useAuthStore();
  const { isSuperAdmin, isImpersonating } = useTenantContext();
  const isProviderAdmin = role === 'provideradmin';
  const { logoUrl, appName, appNameArabic } = useBrandingTheme();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [navigationQuery, setNavigationQuery] = useState('');
  const [collapsedSearchSections, setCollapsedSearchSections] = useState<string[]>([]);
  const sidebarId = useId();
  const navigationToggleRef = useRef<HTMLButtonElement>(null);
  const navigationPanelRef = useRef<HTMLElement>(null);
  const navigationScope = JSON.stringify([role, tenantId, impersonatedTenantId, providerId, user?.userId, isSuperAdmin, isImpersonating]);

  const governance = useTenantGovernance();
  const providerCapabilities = useProviderCapabilities();

  // Tenant's enabled modules â€” hide tenant-admin items for disabled modules.
  const { isManageable, ready: modulesReady, capabilities: tenantCapabilities } = useEnabledModules();

  // Select menu based on role - completely separate menus per role
  const sidebarItems = useMemo<SidebarItem[]>(
    () => {
      if ((isProviderAdmin || role === 'providerstaff') && providerCapabilities.isStaff) return providerCapabilities.ready ? [{ icon: Package, labelKey: 'merchantStaff.workspaceNav', href: '/business/staff-stock' }, { icon: Building2, labelKey: 'membership.title', href: '/business-workspaces' }] : [];
      if (role === 'providerstaff') return [];
      if (isProviderAdmin) return groupProviderSidebar(filterProviderAdminItems(
        providerAdminItems.filter(item => item.href !== '/business/deliveries' || tenantCapabilities.shop),
        providerCapabilities.capabilities,
        providerCapabilities.ready,
      ).map(item => item.href === '/business/orders' && providerCapabilities.capabilities.activeModules.length === 1
        && providerCapabilities.capabilities.activeModules[0] === 'booking' && !providerCapabilities.capabilities.fieldOperations
        ? { ...item, labelKey: 'bookingBusiness.orders' } : item), providerCapabilities.capabilities.activeModules.length === 1 && providerCapabilities.capabilities.activeModules[0] === 'booking' && !providerCapabilities.capabilities.fieldOperations);
      // SuperAdmin on the platform menu (not scoped to a tenant).
      if (isSuperAdmin && !isImpersonating) return groupPlatformSidebar(superAdminItems);
      // Impersonating SuperAdmin OR real tenant admin: show the tenant menu,
      // Apply known presentation limits while loading too, so a service tenant
      // never briefly sees Shop orders or other unrelated module links.
      const moduleFilteredItems = filterTenantItemsByModules(
        tenantAdminItems, key => modulesReady && isManageable(key), tenantCapabilities,
      );
      const privacyItems = (!governance.enabled || (governance.ready && !governance.independent)) ? moduleFilteredItems
        : moduleFilteredItems.map(item => item.children ? { ...item, children: item.children.filter(child => !isPrivateBusinessPath(child.href || '') && child.href !== '/shop-orders') } : item)
          .filter(item => item.children ? item.children.length > 0 : !isPrivateBusinessPath(item.href || '') && item.href !== '/shop-orders');
      return groupTenantSidebar(filterTenantItemsByRole(privacyItems, role).map(item => ({
        ...item,
        children: item.children?.map(child => {
          if (tenantCapabilities.shop && !tenantCapabilities.services) {
            if (child.href === '/providers') return { ...child, labelKey: 'domainUi.shops' };
            if (child.href === '/providers/approval') return { ...child, labelKey: 'domainUi.shopApprovals' };
          }
          return child.href === '/providers' && tenantCapabilities.directBusinesses
            ? { ...child, labelKey: 'domainUi.businesses' } : child;
        }),
      })));
    },
    [isSuperAdmin, isImpersonating, isProviderAdmin, providerCapabilities.isStaff, providerCapabilities.capabilities, providerCapabilities.ready, modulesReady, isManageable, tenantCapabilities, role, governance.enabled, governance.ready, governance.independent]
  );

  const toggleSidebar = () => {
    setIsMobileOpen(!isMobileOpen);
    onClose?.();
  };

  const toggleSection = (labelKey: string) => {
    if (navigationQuery.trim()) {
      setCollapsedSearchSections(current => current.includes(labelKey) ? current.filter(key => key !== labelKey) : [...current, labelKey]);
    } else {
      setExpandedSections(current => current.includes(labelKey) ? [] : [labelKey]);
    }
  };

  const changeNavigationSearch = (value: string) => {
    setNavigationQuery(value);
    setCollapsedSearchSections([]);
  };

  const closeNavigation = () => {
    setIsMobileOpen(false);
    onClose?.();
    changeNavigationSearch('');
    if (isMobile) window.requestAnimationFrame(() => navigationToggleRef.current?.focus());
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const sidebarOpen = isOpen || isMobileOpen;
  const isMobile = useIsMobile();

  const activeHref = activeNavigationHref(sidebarItems, location.pathname);
  const isChildActive = (children: SidebarItem['children']) => !!children?.some(child => child.href === activeHref);
  const activeSectionKey = sidebarItems.find(item => isChildActive(item.children))?.labelKey;
  const CollapseIcon = language === 'ar' ? ChevronLeft : ChevronRight;

  useEffect(() => {
    setExpandedSections(activeSectionKey ? [activeSectionKey] : []);
  }, [activeSectionKey, location.pathname, navigationScope]);

  useEffect(() => {
    setNavigationQuery('');
    setCollapsedSearchSections([]);
  }, [navigationScope]);

  useEffect(() => {
    if (!isMobile || !sidebarOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const frame = window.requestAnimationFrame(() => navigationPanelRef.current?.querySelector<HTMLInputElement>('input[type="search"]')?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsMobileOpen(false);
        onClose?.();
        window.requestAnimationFrame(() => navigationToggleRef.current?.focus());
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = navigationPanelRef.current;
      const stops = Array.from(panel?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), [tabindex="0"]') ?? [])
        .filter(element => !element.closest('[hidden], [inert]'));
      const first = stops[0], last = stops.at(-1);
      if (!first || !last) return;
      if (!panel?.contains(document.activeElement) || event.shiftKey && document.activeElement === first || !event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobile, sidebarOpen, onClose]);

  const visibleSidebarItems = useMemo(() => {
    const query = navigationQuery.trim().toLocaleLowerCase(language);
    if (!query) return sidebarItems;
    return sidebarItems.flatMap((item) => {
      const parentMatches = t(item.labelKey).toLocaleLowerCase(language).includes(query);
      if (!item.children) return parentMatches ? [item] : [];
      const matchingChildren = item.children.filter((child) => t(child.labelKey).toLocaleLowerCase(language).includes(query));
      return parentMatches || matchingChildren.length > 0 ? [{ ...item, children: parentMatches ? item.children : matchingChildren }] : [];
    });
  }, [language, navigationQuery, sidebarItems, t]);

  return (
    <>
      {/* Mobile Trigger */}
      <div hidden={sidebarOpen} className="admin-mobile-navigation-toggle md:hidden fixed top-4 ltr:left-4 rtl:right-20 z-50">
        <Button variant="outline" size="icon" ref={navigationToggleRef} onClick={toggleSidebar} aria-controls={sidebarId} aria-label={t('layout.sidebar.toggleNavigation')} aria-expanded={sidebarOpen}>
          {sidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </Button>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="admin-sidebar-overlay fixed inset-0 z-40 md:hidden"
          onClick={closeNavigation}
        />
      )}

      {/* Sidebar Container */}
      <aside
        id={sidebarId}
        ref={navigationPanelRef}
        inert={isMobile && !sidebarOpen}
        aria-hidden={isMobile && !sidebarOpen ? true : undefined}
        className={cn(
          'fixed top-0 h-full w-[var(--admin-sidebar-width)] bg-sidebar text-sidebar-foreground z-40 transition-transform duration-[var(--admin-transition-standard)] ease-in-out border-sidebar-border shadow-xl',
          'ltr:left-0 ltr:border-r rtl:right-0 rtl:border-l',
          // Always visible on desktop (md+), hide/show on mobile based on sidebarOpen
          'md:translate-x-0',
          !sidebarOpen && 'max-md:ltr:-translate-x-full max-md:rtl:translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo Area */}
          <div className="h-[var(--admin-header-height)] flex items-center px-6 border-b border-sidebar-border gap-3">
            {(!isSuperAdmin || isImpersonating) && !isProviderAdmin && logoUrl ? (
              <img src={logoUrl} alt="" className="h-8 w-8 rounded object-cover" />
            ) : null}
            <div className="text-xl font-bold text-sidebar-foreground tracking-tight">
              {isProviderAdmin ? (
                <>{t(providerCapabilities.isStaff ? 'merchantStaff.workspaceNav' : 'providerAdmin.myBusiness')}</>
              ) : (!isSuperAdmin || isImpersonating) && (language === 'ar' ? appNameArabic || appName : appName || appNameArabic) ? (
                language === 'ar' ? appNameArabic || appName : appName || appNameArabic
              ) : isSuperAdmin ? (
                <>{t('layout.sidebar.platformBrand')}</>
              ) : (
                <>{t('layout.sidebar.dashboardBrand')}</>
              )}
            </div>
            <Button variant="ghost" size="icon" className="ms-auto shrink-0 md:hidden" onClick={closeNavigation} aria-label={t('layout.sidebar.closeNavigation')}><X className="h-4 w-4" aria-hidden="true" /></Button>
          </div>

          {/* Navigation */}
          <nav aria-label={t('layout.sidebar.navigation')} className="flex-1 overflow-y-auto py-6 px-4 space-y-1">
            <div className="relative mb-4">
              <label htmlFor={sidebarId + '-search'} className="sr-only">{t('layout.sidebar.navigationSearch')}</label>
              <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sidebar-foreground/50" aria-hidden="true" />
              <input id={sidebarId + '-search'} type="search" value={navigationQuery} onChange={event => changeNavigationSearch(event.target.value)} placeholder={t('layout.sidebar.navigationSearch')} className="[&::-webkit-search-cancel-button]:appearance-none h-10 w-full rounded-lg border border-sidebar-border bg-sidebar-accent/40 ps-9 pe-9 text-sm text-sidebar-foreground outline-none placeholder:text-sidebar-foreground/50 focus-visible:ring-2 focus-visible:ring-sidebar-ring" />
              {navigationQuery && <button type="button" onClick={() => changeNavigationSearch('')} aria-label={t('layout.sidebar.clearNavigationSearch')} className="absolute end-2 top-1/2 -translate-y-1/2 rounded p-1 text-sidebar-foreground/70 hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"><X className="h-4 w-4" aria-hidden="true" /></button>}
            </div>
            {visibleSidebarItems.length === 0 && <p className="px-4 py-6 text-center text-sm text-sidebar-foreground/60">{t('layout.sidebar.noNavigationResults')}</p>}
            {visibleSidebarItems.map((item) => {
              if (item.children) {
                const isExpanded = navigationQuery.trim() ? !collapsedSearchSections.includes(item.labelKey) : expandedSections.includes(item.labelKey);
                const hasActiveChild = isChildActive(item.children);

                return (
                  <div key={item.labelKey}>
                    <button
                      onClick={() => toggleSection(item.labelKey)}
                      className={cn(
                        'w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors mb-1',
                        hasActiveChild
                          ? 'bg-sidebar-accent/50 text-sidebar-accent-foreground'
                          : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
                      )}
                      aria-expanded={isExpanded}
                      aria-controls={isExpanded ? sidebarId + item.labelKey : undefined}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <item.icon
                          className={cn(
                            'h-5 w-5 shrink-0',
                            hasActiveChild ? 'text-primary' : 'text-muted-foreground'
                          )}
                        />
                        <span className="min-w-0 break-words text-start">{t(item.labelKey)}</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <CollapseIcon className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>

                    {/* Sub-items */}
                    {isExpanded && (
                      <div id={sidebarId + item.labelKey} className="ltr:ml-4 ltr:pl-4 ltr:border-l rtl:mr-4 rtl:pr-4 rtl:border-r border-sidebar-border space-y-1">
                        {item.children.map((child) => {
                          const isChildItemActive =
                            child.href === activeHref;
                          return (
                            <NavLink
                              key={child.href}
                              to={child.href}
                              end={child.href === '/' || child.href === '/business'}
                              onClick={closeNavigation}
                              className={cn(
                                'flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
                                isChildItemActive
                                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                                  : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
                              )}
                              aria-current={isChildItemActive ? 'page' : false}
                            >
                              <child.icon
                                className={cn(
                                  'h-4 w-4 shrink-0',
                                  isChildItemActive ? 'text-primary' : 'text-muted-foreground'
                                )}
                              />
                              <span className="min-w-0 break-words text-start">{t(child.labelKey)}</span>
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              const isActive =
                item.href === activeHref;
              return (
                <NavLink
                  key={item.href}
                  to={item.href!}
                  end={item.href === '/' || item.href === '/business'}
                  onClick={closeNavigation}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors mb-1',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
                  )}
                  aria-current={isActive ? 'page' : false}
                >
                  <item.icon
                    className={cn(
                      'h-5 w-5 shrink-0',
                      isActive ? 'text-primary' : 'text-muted-foreground'
                    )}
                  />
                  <span className="min-w-0 break-words text-start">{t(item.labelKey)}</span>
                </NavLink>
              );
            })}
          </nav>

          {/* Footer Actions */}
          <div className="p-4 border-t border-sidebar-border">
            <Button
              variant="ghost"
              className="w-full justify-start text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
              onClick={handleLogout}
            >
              <LogOut className="me-2 h-4 w-4" />
              {t('layout.sidebar.logout')}
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
}
